import type { RegistrationWindowItem } from "@seagames/contracts";
import { type ColumnDef, DataTable, ListHeader, ListToolbar, PrimaryCell, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { z } from "zod";
import { CategoryBanner } from "../../components/access-codes/category-banner";
import { ConfirmDeleteModal } from "../../components/common/confirm-delete-modal";
import { EditDeleteActions } from "../../components/common/edit-delete-actions";
import { Timestamp } from "../../components/common/list-columns";
import { type RegistrationWindowFormTarget, RegistrationWindowForm } from "../../components/registration-windows/registration-window-form";
import { WindowStatusPill, windowState } from "../../components/registration-windows/window-status";
import { gamesWithDatesOptions, liveGames } from "../../lib/accreditation/register-options";
import { mainFunctionOptionsOptions } from "../../lib/functions/queries";
import { listSearch } from "../../lib/list-page/list-search";
import { useConfirmDelete } from "../../lib/list-page/use-confirm-delete";
import { useListPage } from "../../lib/list-page/use-list-page";
import { deleteRegistrationWindow, invalidateRegistrationWindows, registrationWindowsListOptions } from "../../lib/registration-windows/queries";
import { responsibleOrganizationOptionsOptions } from "../../lib/responsible-organizations/queries";

/** The list has one fixed order (games, category, organisation, opens) — no sort param on the API. */
const SORT_KEYS = ["opens_at"] as const;

export const Route = createFileRoute("/_app/settings/registration-windows")({
	validateSearch: listSearch(SORT_KEYS, {
		games_id: z.string().uuid(),
		main_function_id: z.string().uuid(),
		responsible_organization_id: z.string().uuid(),
	}),
	component: RegistrationWindowsPage,
});

interface WindowFilters extends Record<string, unknown> {
	games: string;
	category: string;
	organisation: string;
}
const EMPTY_FILTERS: WindowFilters = { games: "", category: "", organisation: "" };

type ColumnKey = "games" | "category" | "organisation" | "opens_at" | "closes_at" | "status";

/**
 * Game Settings › Registration windows: when scoped logins may register cards
 * of one category for one games (optionally one organisation). Outside every
 * window the category reads "Registration closed" on Register New Record, and
 * the API refuses create / first submit (403 REGISTRATION_CLOSED). Admins are
 * never window-bound. ADMIN reads, SUPER_ADMIN writes.
 */
function RegistrationWindowsPage() {
	const { t } = useTranslation("registrationWindows");
	const { formatNumber } = useUiStrings();
	const search = Route.useSearch();
	const { session } = Route.useRouteContext();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const [form, setForm] = useState<RegistrationWindowFormTarget | null>(null);
	const games = useQuery(gamesWithDatesOptions());
	const gamesName = (id: string, code: string) => games.data?.find((g) => g.games_id === id)?.name ?? code;
	const categories = useQuery(mainFunctionOptionsOptions());
	const orgs = useQuery(responsibleOrganizationOptionsOptions());

	const page = useListPage({
		search,
		navigate: Route.useNavigate(),
		listOptions: (q) =>
			registrationWindowsListOptions({
				page: q.page,
				page_size: q.page_size,
				games_id: search.games_id,
				main_function_id: search.main_function_id,
				responsible_organization_id: search.responsible_organization_id,
			}),
		columnsKey: "registration-windows",
		filters: {
			empty: EMPTY_FILTERS,
			fromUrl: (s): WindowFilters => ({ games: s.games_id ?? "", category: s.main_function_id ?? "", organisation: s.responsible_organization_id ?? "" }),
			toUrl: (f) => ({ games_id: f.games || undefined, main_function_id: f.category || undefined, responsible_organization_id: f.organisation || undefined }),
		},
		summary: { range: (v) => t("pagination.summary", v), empty: t("pagination.empty") },
	});
	const total = page.list.data?.total;
	const colorOf = (id: string) => categories.data?.find((c) => c.main_function_id === id);
	const now = Date.now();

	const del = useConfirmDelete<RegistrationWindowItem>({
		remove: (r) => deleteRegistrationWindow(r.registration_window_id, `Deleted registration window ${r.games.code} ${r.main_function.code}`),
		invalidate: invalidateRegistrationWindows,
		deleted: () => t("delete.deleted"),
	});

	const columns: ColumnDef<RegistrationWindowItem, ColumnKey>[] = [
		{
			key: "category",
			header: t("table.category"),
			lockVisible: true,
			minWidth: "13rem",
			cell: (r) => {
				const c = colorOf(r.main_function.id);
				return (
					<div className="flex items-center gap-[0.5rem]">
						<CategoryBanner code={r.main_function.code} cardColor={c?.card_color ?? null} inkColor={c?.card_ink_color ?? null} title={r.main_function.name} />
						<PrimaryCell>{r.main_function.name}</PrimaryCell>
					</div>
				);
			},
		},
		{ key: "games", header: t("table.games"), minWidth: "8rem", cell: (r) => <span className="font-medium" title={r.games.code}>{gamesName(r.games.id, r.games.code)}</span> },
		{
			key: "organisation",
			header: t("table.organisation"),
			minWidth: "10rem",
			cell: (r) => (r.responsible_organization ? r.responsible_organization.name : <span className="text-base-content/55">{t("table.allOrganisations")}</span>),
		},
		{ key: "opens_at", header: t("table.opens"), minWidth: "9rem", cell: (r) => <Timestamp iso={r.opens_at} /> },
		{ key: "closes_at", header: t("table.closes"), minWidth: "9rem", cell: (r) => <Timestamp iso={r.closes_at} /> },
		{ key: "status", header: t("table.status"), minWidth: "7rem", cell: (r) => <WindowStatusPill state={windowState(r, now)} /> },
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader
				title={t("page.title")}
				count={total === undefined ? undefined : t("page.count", { count: total, n: formatNumber(total) })}
				description={t("page.intro")}
				actionLabel={t("page.add")}
				onAction={isSuper ? () => setForm({ mode: "create", gamesId: search.games_id }) : undefined}
			/>

			<ListToolbar
				chips={[
					{
						kind: "options",
						key: "games",
						label: t("filters.games"),
						// Ended games leave the chips (a selected one stays, so it can be cleared); their windows still list unfiltered.
						options: liveGames(games.data, search.games_id).map((g) => ({ value: g.games_id, label: g.name })),
						value: search.games_id ? [search.games_id] : [],
						onChange: (v) => page.patch({ games_id: v[0] ?? undefined }),
					},
					{
						kind: "menu",
						key: "category",
						label: t("filters.category"),
						options: (categories.data ?? []).map((c) => ({ value: c.main_function_id, label: `${c.main_function_name} (${c.main_function_code})` })),
						value: search.main_function_id ?? null,
						onChange: (v) => page.patch({ main_function_id: v ?? undefined }),
						searchPlaceholder: t("filters.search"),
					},
					{
						kind: "menu",
						key: "organisation",
						label: t("filters.organisation"),
						options: (orgs.data ?? []).map((o) => ({ value: o.responsible_organization_id, label: o.name })),
						value: search.responsible_organization_id ?? null,
						onChange: (v) => page.patch({ responsible_organization_id: v ?? undefined }),
						searchPlaceholder: t("filters.search"),
					},
				]}
				onClearAll={page.clearFilters}
			/>

			<DataTable<RegistrationWindowItem, ColumnKey>
				{...page.table}
				variant="list"
				columns={columns}
				rowKey={(r) => r.registration_window_id}
				empty={page.emptyState({
					filtered: { title: t("empty.filteredTitle"), description: t("empty.filteredDescription") },
					none: { title: t("empty.noneTitle"), description: t("empty.noneDescription") },
					clearLabel: t("empty.clearFilters"),
				})}
				rowActions={
					isSuper
						? (r) => (
								<EditDeleteActions
									editLabel={t("table.editLabel", { name: r.main_function.name })}
									onEdit={() => setForm({ mode: "edit", window: r })}
									deleteLabel={t("table.deleteLabel", { name: r.main_function.name })}
									onDelete={() => del.ask(r)}
									variant="list"
								/>
							)
						: undefined
				}
			/>

			{isSuper && <RegistrationWindowForm target={form} onClose={() => setForm(null)} />}
			{isSuper && (
				<ConfirmDeleteModal
					{...del.modal}
					title={t("delete.title")}
					description={
						<Trans
							t={t}
							i18nKey="delete.description"
							values={{ category: del.row?.main_function.name ?? "", games: del.row?.games.code ?? "" }}
							components={{ strong: <strong className="font-semibold text-base-content" /> }}
						/>
					}
					confirmLabel={t("delete.confirm")}
					errorPrefix={t("delete.failed")}
				/>
			)}
		</div>
	);
}
